import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/fetcher';
import { ApiEndpoint } from '@/api/registry';
import { POLICY_IDS } from '@/features/decisions/config/personaContext';
import type { FcfEntitlementBar, FcfEntitlementResponse } from '@/features/decisions/decisions.api.types';
import { computeDeltas, type DeltaBuildup } from '@/features/decisions/utils/fcfFormat';
import { useTenant } from '@/providers/TenantProvider';

export interface UseFcfEntitlementParams {
  scenarioRunId: string;
  runId: string;
  refExperimentId: string;
  experimentIds: string[];
  periodFrom: string;
  periodTo: string;
}

// Owns the chart's data: fetches bars + cards, splits ref vs comparison, computes deltas.
export function useFcfEntitlement({
  scenarioRunId,
  runId,
  refExperimentId,
  experimentIds,
  periodFrom,
  periodTo,
}: UseFcfEntitlementParams) {
  const { tenantAlias } = useTenant();

  const query = useQuery<FcfEntitlementResponse, Error>({
    queryKey: [
      'fcf-entitlement',
      tenantAlias,
      scenarioRunId,
      runId,
      refExperimentId,
      experimentIds,
      periodFrom,
      periodTo,
    ],
    enabled: Boolean(
      scenarioRunId &&
      runId &&
      refExperimentId &&
      experimentIds.length > 0 &&
      periodFrom &&
      periodTo &&
      periodFrom <= periodTo,
    ),
    retry: false,
    staleTime: 30_000,
    queryFn: () =>
      apiFetch<FcfEntitlementResponse>(tenantAlias, ApiEndpoint.SimulationFcfEntitlement, {
        pathParams: { scenarioRunId, runId },
        query: {
          ref_experiment_id: refExperimentId,
          experiment_id: experimentIds,
          period_from: periodFrom,
          period_to: periodTo,
        },
      }),
  });

  const bars = useMemo<FcfEntitlementBar[]>(() => query.data?.bars ?? [], [query.data]);
  const refBar = useMemo(() => bars.find((b) => b.is_reference), [bars]);
  const cmpBars = useMemo(() => {
    const order = new Map(experimentIds.map((id, i) => [id, i]));
    return bars
      .filter((b) => !b.is_reference)
      .sort((a, b) => (order.get(a.experiment_id) ?? Infinity) - (order.get(b.experiment_id) ?? Infinity));
  }, [bars, experimentIds]);
  // The "Optimized vs Incumbent" build-up + headline KPI compare against the
  // optimized policy specifically (fall back to the last comparison).
  const headlineCmp =
    cmpBars.find((b) => b.experiment_id === POLICY_IDS.keystoneOptimized) ?? cmpBars[cmpBars.length - 1];
  const deltas = useMemo<DeltaBuildup | null>(
    () => (refBar && headlineCmp ? computeDeltas(refBar, headlineCmp) : null),
    [refBar, headlineCmp],
  );

  return {
    bars,
    cards: query.data?.cards,
    resolved: query.data?.resolved,
    refBar,
    cmpBars,
    headlineCmp,
    deltas,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}
